import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { useI18n } from "@/i18n/I18nProvider";
import { supabase } from "@/integrations/supabase/client";
import { Reveal } from "@/hooks/useReveal";
import { SEO } from "@/components/SEO";
import { Magnetic } from "@/components/MagneticButton";
import {
  ArrowUpRight,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  FileSignature,
  Sparkles,
  User,
  Lightbulb,
  X,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const projectTypeKeys = ["ai", "web", "mobile", "chain", "automation", "other"] as const;
const budgetKeys = ["a", "b", "c", "d", "e"] as const;
const timelineKeys = ["asap", "q1", "q2", "q3", "flex"] as const;
const industryKeys = [
  "finance",
  "health",
  "retail",
  "saas",
  "education",
  "media",
  "energy",
  "gov",
  "manufacturing",
  "logistics",
  "realestate",
  "other",
] as const;

const roleKeys = [
  "founder",
  "ceo",
  "cto",
  "coo",
  "cfo",
  "cmo",
  "product",
  "engineering",
  "design",
  "operations",
  "marketing",
  "sales",
  "innovation",
  "consultant",
  "investor",
  "other",
] as const;

type FormState = {
  name: string;
  email: string;
  company: string;
  role: (typeof roleKeys)[number] | "";
  projectType: (typeof projectTypeKeys)[number][];
  industry: (typeof industryKeys)[number] | "";
  budget: (typeof budgetKeys)[number] | "";
  timeline: (typeof timelineKeys)[number] | "";
  message: string;
};

const initial: FormState = {
  name: "",
  email: "",
  company: "",
  role: "",
  projectType: [],
  industry: "",
  budget: "",
  timeline: "",
  message: "",
};

export default function Contact() {
  const { t, lang } = useI18n();
  const W = (t.contact as any).wizard as any;
  const e = t.contact.errors as Record<string, string>;

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [ndaOpen, setNdaOpen] = useState(false);
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [ndaAcceptedAt, setNdaAcceptedAt] = useState<Date | null>(null);
  const [data, setData] = useState<FormState>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [canSubmit, setCanSubmit] = useState(false);

  useEffect(() => {
    if (step !== 4) {
      setCanSubmit(false);
      return;
    }
    const id = window.setTimeout(() => setCanSubmit(true), 1200);
    return () => window.clearTimeout(id);
  }, [step]);

  // ---------- schemas (per step) ----------
  const step1Schema = z.object({
    name: z
      .string()
      .trim()
      .min(1, e.nameRequired)
      .min(2, e.nameTooShort)
      .max(100),
    email: z.string().trim().email(e.emailInvalid).max(255),
    company: z.string().trim().min(1, e.companyRequired).max(120),
    role: z.enum(roleKeys, {
      errorMap: () => ({ message: (W as any).roleRequired ?? e.companyRequired }),
    }),
  });

  const step3Schema = z.object({
    projectType: z
      .array(z.enum(projectTypeKeys))
      .min(1, e.projectTypeRequired),
    industry: z.enum(industryKeys, {
      errorMap: () => ({ message: (e as any).industryRequired ?? e.projectTypeRequired }),
    }),
    budget: z.enum(budgetKeys, { errorMap: () => ({ message: e.budgetRequired }) }),
    timeline: z.enum(timelineKeys, { errorMap: () => ({ message: e.timelineRequired }) }),
    message: z
      .string()
      .trim()
      .min(1, e.messageRequired)
      .min(20, e.messageTooShort)
      .max(7000),
  });

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setData((d) => ({ ...d, [key]: value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validateStep = (s: 1 | 2 | 3): boolean => {
    if (s === 1) {
      const parsed = step1Schema.safeParse({
        name: data.name,
        email: data.email,
        company: data.company,
        role: data.role,
      });
      if (!parsed.success) {
        const fe: Partial<Record<keyof FormState, string>> = {};
        parsed.error.issues.forEach((i) => {
          const k = i.path[0] as keyof FormState;
          if (!fe[k]) fe[k] = i.message;
        });
        setErrors((prev) => ({ ...prev, ...fe }));
        return false;
      }
      return true;
    }
    if (s === 2) return ndaAccepted;
    if (s === 3) {
      const parsed = step3Schema.safeParse({
        projectType: data.projectType,
        industry: data.industry,
        budget: data.budget,
        timeline: data.timeline,
        message: data.message,
      });
      if (!parsed.success) {
        const fe: Partial<Record<keyof FormState, string>> = {};
        parsed.error.issues.forEach((i) => {
          const k = i.path[0] as keyof FormState;
          if (!fe[k]) fe[k] = i.message;
        });
        setErrors((prev) => ({ ...prev, ...fe }));
        return false;
      }
      return true;
    }
    return true;
  };

  const wizardRef = useRef<HTMLDivElement>(null);
  const scrollToWizard = () => {
    queueMicrotask(() => {
      const el = wizardRef.current;
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top: y, behavior: "smooth" });
    });
  };

  const goNext = () => {
    if (step < 4 && !validateStep(step as 1 | 2 | 3)) return;
    setStep((s) => (Math.min(4, s + 1) as 1 | 2 | 3 | 4));
    scrollToWizard();
  };

  const goBack = () => {
    setStep((s) => (Math.max(1, s - 1) as 1 | 2 | 3 | 4));
    scrollToWizard();
  };

  const onSubmit = async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    if (!canSubmit) return;
    // re-validate all gates
    if (!validateStep(1)) { setStep(1); return; }
    if (!ndaAccepted) { setStep(2); return; }
    if (!validateStep(3)) { setStep(3); return; }

    setSubmitting(true);
    try {
      const projectTypeLabels = (data.projectType || []).map(
        (k) => (t.contact.projectTypes as Record<string, string>)[k] ?? k,
      );
      const roleLabel = data.role
        ? ((W as any).roles as Record<string, string>)[data.role] ?? data.role
        : "";
      const industryLabel = data.industry
        ? ((W as any).industries as Record<string, string>)[data.industry] ?? data.industry
        : "";
      const budgetLabel = data.budget
        ? ((W as any).monthlyBudgets as Record<string, string>)[data.budget] ?? data.budget
        : "";
      const timelineLabel = data.timeline
        ? (t.contact.timelines as Record<string, string>)[data.timeline] ?? data.timeline
        : "";
      await supabase.functions.invoke("submit-contact-brief", {
        body: {
          language: lang,
          name: data.name,
          email: data.email,
          company: data.company,
          role: roleLabel,
          projectType: projectTypeLabels,
          industry: industryLabel,
          budget: budgetLabel,
          timeline: timelineLabel,
          message: data.message,
          ndaClauses: W.ndaClauses,
          ndaConsent: W.ndaConsent,
          ndaAcceptedAt: (ndaAcceptedAt ?? new Date()).toISOString(),
        },
      });
      setSubmitted(true);
      toast({ title: t.contact.formSent, description: t.contact.formSentDesc });
    } catch (err) {
      console.error("submit-contact-brief failed", err);
      toast({
        title: t.contact.formSent,
        description: t.contact.formSentDesc,
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const acceptNda = () => {
    setNdaAccepted(true);
    setNdaAcceptedAt(new Date());
    setNdaOpen(false);
  };

  const acceptedDate = useMemo(() => {
    if (!ndaAcceptedAt) return "";
    return ndaAcceptedAt.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, [ndaAcceptedAt]);

  const fillTemplate = (tpl: string, vars: Record<string, string>) =>
    tpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? "");

  const ndaVars = {
    company: data.company || "—",
    name: data.name || "—",
    email: data.email || "—",
    date: acceptedDate || "—",
  };

  const resetAll = () => {
    setData(initial);
    setErrors({});
    setNdaAccepted(false);
    setNdaAcceptedAt(null);
    setSubmitted(false);
    setStep(1);
  };

  return (
    <>
      <SEO title={t.nav.contact} description={t.contact.subtitle} />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--foreground)/0.05),transparent_60%)]" />
          <div className="absolute inset-0 bg-grid-fine opacity-40 mask-radial-fade" />
        </div>
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-24 lg:pt-32 pb-16">
          <Reveal>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-6">
              — {t.contact.eyebrow}
            </p>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-display text-5xl lg:text-8xl font-semibold tracking-tighter leading-[0.95] max-w-5xl text-balance">
              {t.contact.title}
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <p className="mt-8 max-w-2xl text-lg lg:text-xl text-muted-foreground leading-relaxed">
              {t.contact.subtitle}
            </p>
          </Reveal>
        </div>
      </section>

      {/* WIZARD */}
      <section className="border-t hairline">
        <div className="mx-auto max-w-4xl px-6 lg:px-10 py-16 lg:py-24">
          {submitted ? (
            <Reveal>
              <div className="rounded-3xl border hairline bg-foreground/[0.012] p-8 sm:p-12 lg:p-16 text-center">
                <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full border hairline bg-background">
                  <CheckCircle2 className="h-7 w-7" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tighter">
                  {W.submittedTitle}
                </h2>
                <p className="mt-5 mx-auto max-w-xl text-base lg:text-lg text-muted-foreground leading-relaxed">
                  {(() => {
                    const parts = (W.submittedBody as string).split("{email}");
                    return (
                      <>
                        {parts[0]}
                        <span className="text-foreground font-medium">{data.email}</span>
                        {parts[1] ?? ""}
                      </>
                    );
                  })()}
                </p>
                <button
                  type="button"
                  onClick={resetAll}
                  className="mt-10 inline-flex h-11 items-center gap-2 rounded-full border hairline px-5 text-sm hover:bg-foreground/[0.04]"
                >
                  {W.submittedAgain}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <div ref={wizardRef} className="rounded-3xl border hairline bg-foreground/[0.012] overflow-hidden scroll-mt-24">
                {/* Stepper */}
                <ProgressHeader step={step} total={4} W={W} />

                <form onSubmit={onSubmit} noValidate className="px-6 sm:px-10 lg:px-12 pb-10 pt-2">
                  {/* STEP 1 */}
                  {step === 1 && (
                    <StepShell
                      
                      icon={User}
                      title={W.step1Title}
                      subtitle={W.step1Sub}
                    >
                      <div className="grid sm:grid-cols-2 gap-6">
                        <Field
                          name="name"
                          label={t.contact.formName}
                          placeholder={t.contact.placeholderName}
                          value={data.name}
                          error={errors.name}
                          onChange={(v) => set("name", v)}
                          required
                        />
                        <Field
                          name="email"
                          type="email"
                          label={t.contact.formEmail}
                          placeholder={t.contact.placeholderEmail}
                          value={data.email}
                          error={errors.email}
                          onChange={(v) => set("email", v)}
                          required
                        />
                      </div>
                      <Field
                        name="company"
                        label={t.contact.formCompany}
                        placeholder={t.contact.placeholderCompany}
                        value={data.company}
                        error={errors.company}
                        onChange={(v) => set("company", v)}
                        required
                      />

                      <Select
                        label={(W as any).formRole}
                        placeholder={(W as any).rolePlaceholder}
                        value={data.role}
                        error={errors.role as string | undefined}
                        onChange={(v) => set("role", v as FormState["role"])}
                        options={roleKeys.map((k) => ({
                          value: k,
                          label: ((W as any).roles as Record<string, string>)[k],
                        }))}
                        required
                      />

                      <HintRow
                        icon={ShieldCheck}
                        text={W.step1NdaHint}
                      />
                    </StepShell>
                  )}

                  {/* STEP 2 — NDA */}
                  {step === 2 && (
                    <StepShell
                      
                      icon={ShieldCheck}
                      title={W.step2Title}
                      subtitle={W.step2Sub}
                    >
                      <button
                        type="button"
                        onClick={() => setNdaOpen(true)}
                        className={cn(
                          "group w-full text-left rounded-2xl border hairline bg-background p-6 sm:p-8 transition-all duration-300",
                          "hover:bg-foreground/[0.03] hover:border-foreground/30",
                          ndaAccepted && "border-foreground/40 bg-foreground/[0.03]",
                        )}
                      >
                        <div className="flex items-start justify-between gap-6">
                          <div className="flex items-start gap-4 min-w-0">
                            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border hairline bg-background">
                              <FileSignature className="h-5 w-5" strokeWidth={1.5} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">
                                {W.ndaCardKicker}
                              </p>
                              <p className="font-display text-xl lg:text-2xl tracking-tight truncate">
                                {fillTemplate(W.ndaCardTitle, { company: data.company || "—" })}
                              </p>
                              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                {W.ndaCardSub}
                              </p>
                            </div>
                          </div>
                          <ArrowUpRight
                            className="h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                            strokeWidth={1.5}
                          />
                        </div>

                        {ndaAccepted && (
                          <div className="mt-6 flex items-start gap-3 rounded-xl border border-emerald-500/40 bg-emerald-500/[0.06] px-4 py-3">
                            <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" strokeWidth={1.8} />
                            <div className="text-sm">
                              <p className="font-medium text-emerald-700 dark:text-emerald-300">{W.ndaAcceptedBadge}</p>
                              <p className="text-muted-foreground text-xs mt-0.5">
                                {fillTemplate(W.ndaAcceptedSub, ndaVars)}
                              </p>
                            </div>
                          </div>
                        )}
                      </button>
                    </StepShell>
                  )}

                  {/* STEP 3 — Project */}
                  {step === 3 && (
                    <StepShell
                      
                      icon={Lightbulb}
                      title={W.step3Title}
                      subtitle={W.step3Sub}
                    >
                      <ChipGroup
                        label={t.contact.formProjectType}
                        error={errors.projectType}
                        multi
                        values={data.projectType}
                        onMultiChange={(vs) => set("projectType", vs as FormState["projectType"])}
                        options={projectTypeKeys.map((k) => ({
                          value: k,
                          label: (t.contact.projectTypes as Record<string, string>)[k],
                        }))}
                      />

                      <div className="border-t hairline opacity-50" />

                      <ChipGroup
                        label={(W as any).formIndustry}
                        error={errors.industry}
                        value={data.industry}
                        onChange={(v) => set("industry", v as FormState["industry"])}
                        options={industryKeys.map((k) => ({
                          value: k,
                          label: ((W as any).industries as Record<string, string>)[k],
                        }))}
                      />

                      <div className="border-t hairline opacity-50" />

                      <div className="grid sm:grid-cols-2 gap-6">
                        <Select
                          label={W.formMonthlyBudget}
                          placeholder={t.contact.selectPlaceholder}
                          value={data.budget}
                          error={errors.budget}
                          onChange={(v) => set("budget", v as FormState["budget"])}
                          options={budgetKeys.map((k) => ({
                            value: k,
                            label: (W.monthlyBudgets as Record<string, string>)[k],
                          }))}
                          required
                        />
                        <Select
                          label={t.contact.formTimeline}
                          placeholder={t.contact.selectPlaceholder}
                          value={data.timeline}
                          error={errors.timeline}
                          onChange={(v) => set("timeline", v as FormState["timeline"])}
                          options={timelineKeys.map((k) => ({
                            value: k,
                            label: (t.contact.timelines as Record<string, string>)[k],
                          }))}
                          required
                        />
                      </div>

                      <div className="border-t hairline opacity-50" />

                      <div>
                        <label
                          htmlFor="message"
                          className={cn(
                            "block text-xs uppercase tracking-wider text-muted-foreground mb-3 transition-colors",
                            errors.message && "text-destructive",
                          )}
                        >
                          {t.contact.formMessage} *
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          rows={5}
                          maxLength={7000}
                          value={data.message}
                          onChange={(ev) => set("message", ev.target.value)}
                          placeholder={t.contact.placeholderMessage}
                          className={cn(
                            "w-full rounded-xl border hairline bg-background px-4 py-3 text-base outline-none transition-colors resize-none placeholder:text-muted-foreground/40 focus:border-foreground",
                            errors.message && "!border-destructive focus:!border-destructive field-shake",
                          )}
                          aria-invalid={!!errors.message}
                        />
                        <div className="mt-2 flex items-center justify-end min-h-[16px]">
                          <span className="text-[10px] font-mono text-muted-foreground/60 tabular-nums">
                            {data.message.length} / 7000
                          </span>
                        </div>
                      </div>
                    </StepShell>
                  )}

                  {/* STEP 4 — Epic close */}
                  {step === 4 && (
                    <StepShell
                      
                      icon={Sparkles}
                      title={W.step4Title}
                      subtitle={W.step4Body}
                    >
                      <div className="rounded-2xl border hairline bg-background p-6 lg:p-8">
                        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-5">
                          {W.step4Recap}
                        </p>
                        <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                          <RecapRow k={W.step4RecapName} v={`${data.name} · ${data.email}`} />
                          <RecapRow k={W.step4RecapCompany} v={data.company} />
                          <RecapRow
                            k={W.step4RecapNda}
                            v={
                              ndaAccepted
                                ? `${W.ndaAcceptedBadge} · ${acceptedDate}`
                                : "—"
                            }
                          />
                          <RecapRow
                            k={W.step4RecapType}
                            v={
                              data.projectType.length > 0
                                ? data.projectType
                                    .map((p) => (t.contact.projectTypes as Record<string, string>)[p])
                                    .join(", ")
                                : "—"
                            }
                          />
                          <RecapRow
                            k={(W as any).step4RecapIndustry}
                            v={
                              data.industry
                                ? ((W as any).industries as Record<string, string>)[data.industry]
                                : "—"
                            }
                          />
                          <RecapRow
                            k={W.step4RecapBudget}
                            v={
                              data.budget
                                ? (W.monthlyBudgets as Record<string, string>)[data.budget]
                                : "—"
                            }
                          />
                          <RecapRow
                            k={W.step4RecapTimeline}
                            v={
                              data.timeline
                                ? (t.contact.timelines as Record<string, string>)[data.timeline]
                                : "—"
                            }
                          />
                        </dl>
                      </div>
                    </StepShell>
                  )}

                  {/* Footer nav */}
                  <div className="mt-10 flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-4 border-t hairline pt-6">
                    {step > 1 && (
                      <button
                        type="button"
                        onClick={goBack}
                        className="inline-flex h-11 items-center gap-2 rounded-full border hairline px-5 text-sm transition-colors hover:bg-foreground/[0.04]"
                      >
                        <ArrowLeft className="h-4 w-4" />
                        {W.back}
                      </button>
                    )}
                    {step === 1 && <div />}

                    {step < 4 ? (
                      <Magnetic>
                        {step === 2 && !ndaAccepted ? (
                          <TooltipProvider delayDuration={100}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex">
                                  <button
                                    type="button"
                                    disabled
                                    className="group inline-flex h-12 items-center gap-2 rounded-full bg-foreground px-7 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-40"
                                  >
                                    {W.next}
                                    <ArrowRight className="h-4 w-4" />
                                  </button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" sideOffset={8}>
                                <p className="max-w-xs text-xs">{W.ndaRequiredTooltip}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <button
                            type="button"
                            onClick={goNext}
                            className="group inline-flex h-12 items-center gap-2 rounded-full bg-foreground px-7 text-sm font-medium text-background transition-opacity hover:opacity-90"
                          >
                            {W.next}
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                          </button>
                        )}
                      </Magnetic>
                    ) : (
                      <Magnetic>
                        <button
                          type="submit"
                          disabled={submitting || !canSubmit}
                          className="group inline-flex h-12 items-center gap-2 rounded-full bg-foreground px-7 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
                        >
                          {submitting ? "…" : W.step4Send}
                          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                      </Magnetic>
                    )}
                  </div>
                </form>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* NDA Sheet */}
      <Sheet open={ndaOpen} onOpenChange={setNdaOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl lg:max-w-2xl p-0 flex flex-col gap-0"
        >
          <SheetHeader className="px-6 sm:px-8 pt-8 pb-6 border-b hairline space-y-2">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
              {W.ndaCardKicker}
            </p>
            <SheetTitle className="font-display text-2xl lg:text-3xl tracking-tight">
              {W.ndaSheetTitle}
            </SheetTitle>
            <SheetDescription className="text-sm text-muted-foreground leading-relaxed">
              {W.ndaSheetDesc}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6 space-y-6 text-sm leading-relaxed">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-3">
                {W.ndaPartiesTitle}
              </p>
              <ul className="space-y-2 text-foreground">
                <li>• {W.ndaPartyA}</li>
                <li>• {fillTemplate(W.ndaPartyB, ndaVars)}</li>
              </ul>
              <p className="mt-3 text-xs text-muted-foreground">
                {fillTemplate(W.ndaEffective, {
                  date:
                    acceptedDate ||
                    new Date().toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    }),
                })}
              </p>
            </div>

            <div className="space-y-5">
              {(W.ndaClauses as { h: string; b: string }[]).map((c) => (
                <div key={c.h}>
                  <p className="font-medium text-foreground">{c.h}</p>
                  <p className="mt-1 text-muted-foreground">{c.b}</p>
                </div>
              ))}
            </div>

            <p className="text-xs text-muted-foreground italic border-t hairline pt-4">
              {W.ndaConsent}
            </p>
          </div>

          <div className="flex items-center justify-between gap-3 border-t hairline px-6 sm:px-8 py-5 bg-background">
            <button
              type="button"
              onClick={() => setNdaOpen(false)}
              className="inline-flex h-11 items-center gap-2 rounded-full border hairline px-5 text-sm hover:bg-foreground/[0.04]"
            >
              <X className="h-4 w-4" />
              {W.ndaCancelBtn}
            </button>
            <Magnetic>
              <button
                type="button"
                onClick={acceptNda}
                className="group inline-flex h-12 items-center gap-2 rounded-full bg-foreground px-6 text-sm font-medium text-background hover:opacity-90"
              >
                <CheckCircle2 className="h-4 w-4" />
                {W.ndaAcceptBtn}
              </button>
            </Magnetic>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

/* -------- Wizard chrome -------- */

function ProgressHeader({
  step,
  total,
  W,
}: {
  step: 1 | 2 | 3 | 4;
  total: number;
  W: any;
}) {
  const pct = (step / total) * 100;
  return (
    <div className="px-6 sm:px-10 lg:px-12 pt-8 pb-6">
      <div className="mb-3">
        <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
          {W.stepLabel} {String(step).padStart(2, "0")} {W.of} {String(total).padStart(2, "0")}
        </span>
      </div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-foreground/[0.08]">
        <div
          className="h-full bg-foreground transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function StepShell({
  kicker,
  icon: Icon,
  title,
  subtitle,
  children,
}: {
  kicker?: string;
  icon: any;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-8 pt-8">
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border hairline bg-background">
          <Icon className="h-5 w-5" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          {kicker && (
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">
              {kicker}
            </p>
          )}
          <h2 className="font-display text-2xl lg:text-3xl tracking-tight">{title}</h2>
          {subtitle && (
            <p className="mt-2 text-sm lg:text-base text-muted-foreground leading-relaxed max-w-2xl">
              {subtitle}
            </p>
          )}
        </div>
      </div>
      <div className="space-y-6">{children}</div>
    </div>
  );
}

function HintRow({ icon: Icon, text }: { icon: any; text: string }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border hairline bg-background/60 p-4 sm:p-5">
      <Icon className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" strokeWidth={1.6} />
      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{text}</p>
    </div>
  );
}

function RecapRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b hairline pb-3 last:border-0 last:pb-0">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{k}</dt>
      <dd className="text-sm text-foreground text-right truncate max-w-[60%]">{v || "—"}</dd>
    </div>
  );
}

/* -------- Premium form primitives -------- */

function Field({
  name,
  label,
  type = "text",
  placeholder,
  value,
  error,
  onChange,
  required,
}: {
  name: string;
  label: string;
  type?: string;
  placeholder?: string;
  value: string;
  error?: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <div>
      <label
        htmlFor={name}
        className={cn(
          "block text-xs uppercase tracking-wider text-muted-foreground mb-3 transition-colors",
          error && "text-destructive",
        )}
      >
        {label}
        {required && " *"}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={
          name === "email" ? "email" : name === "name" ? "name" : name === "company" ? "organization" : "off"
        }
        maxLength={type === "email" ? 255 : 120}
        className={cn(
          "w-full rounded-xl border hairline bg-background px-4 py-3 text-base outline-none transition-colors placeholder:text-muted-foreground/40 focus:border-foreground",
          error && "!border-destructive focus:!border-destructive field-shake",
        )}
        aria-invalid={!!error}
      />
    </div>
  );
}

function ChipGroup({
  label,
  options,
  value,
  onChange,
  error,
  multi,
  values,
  onMultiChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value?: string;
  onChange?: (v: string) => void;
  error?: string;
  multi?: boolean;
  values?: string[];
  onMultiChange?: (vs: string[]) => void;
}) {
  return (
    <div>
      <span
        className={cn(
          "block text-xs uppercase tracking-wider text-muted-foreground mb-3 transition-colors",
          error && "text-destructive",
        )}
      >
        {label} *
      </span>
      <div className={cn("flex flex-wrap gap-2", error && "field-shake")}>
        {options.map((o) => {
          const active = multi
            ? (values ?? []).includes(o.value)
            : value === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => {
                if (multi) {
                  const set = new Set(values ?? []);
                  if (set.has(o.value)) set.delete(o.value);
                  else set.add(o.value);
                  onMultiChange?.(Array.from(set));
                } else {
                  onChange?.(o.value);
                }
              }}
              className={cn(
                "inline-flex h-9 items-center rounded-full border px-4 text-sm transition-all duration-300",
                active
                  ? "bg-foreground text-background border-foreground"
                  : "border-[hsl(var(--hairline))] text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04]",
              )}
              aria-pressed={active}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Select({
  label,
  options,
  value,
  onChange,
  placeholder,
  required,
  error,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
}) {
  return (
    <div>
      <label
        className={cn(
          "block text-xs uppercase tracking-wider text-muted-foreground mb-3 transition-colors",
          error && "text-destructive",
        )}
      >
        {label}
        {required && " *"}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            "w-full appearance-none rounded-xl border hairline bg-background px-4 py-3 pr-10 text-base outline-none transition-colors focus:border-foreground",
            !value && "text-muted-foreground/60",
            error && "!border-destructive focus:!border-destructive field-shake",
          )}
          aria-invalid={!!error}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((o) => (
            <option key={o.value} value={o.value} className="text-foreground">
              {o.label}
            </option>
          ))}
        </select>
        <svg
          className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
        >
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}